/**
 * Credits (docs/spec/07 §2): the game, its star, how it was made, and the tribute - the one place
 * the original game is named.
 */

import { GAME_NAME, HERO_NAME, TRIBUTE } from '../../app/brand';
import { atlasFor } from '../../render/atlas';
import { LIGHT_RED } from '../../render/palette';
import type { Screen } from '../../render/screen';
import type { MenuAction, SceneHost } from '../scene';
import { UiScene } from '../kit/base';
import type { LogoArt } from '../kit/logo';
import { drawLogo, logoFor } from '../kit/logo';
import { wrap } from '../kit/text';
import { animFrame, CAVE_A, FAINT, INK, MUTED, SELECTED } from '../kit/theme';

const RUN_MS = 5200;

export class CreditsScene extends UiScene {
  protected readonly heading = 'CREDITS';
  private readonly art: LogoArt;

  constructor(host: SceneHost) {
    super(host);
    this.art = logoFor(GAME_NAME);
  }

  protected override describe(): string {
    const made = 'All graphics, sound and music are generated from code.';
    return `Credits. ${GAME_NAME}, starring ${HERO_NAME}. ${made} ${TRIBUTE}`;
  }

  protected override hint(): string {
    return `V${this.services.version}   ·   ${this.words().back} BACK`;
  }

  protected paint(screen: Screen): void {
    drawLogo(screen, this.art, Math.floor((320 - this.art.w) / 2), 22, this.ms, null);
    this.paintDigby(screen);
    screen.text(`STARRING ${HERO_NAME}`, 160, 90, SELECTED, 'center');
    screen.text('ALL GRAPHICS, SOUND AND MUSIC', 160, 104, INK, 'center');
    screen.text('ARE GENERATED FROM CODE.', 160, 113, INK, 'center');
    wrap(TRIBUTE, 38).slice(0, 6).forEach((line, i) => screen.text(line, 160, 128 + i * 9, MUTED, 'center'));
    screen.text('THANK YOU FOR PLAYING', 156, 180, FAINT, 'center');
    screen.text('♥', 248, 180, LIGHT_RED);
  }

  /** Digby trots back and forth under the logo. */
  private paintDigby(screen: Screen): void {
    const t = this.ms % (RUN_MS * 2);
    const right = t < RUN_MS;
    const p = (t % RUN_MS) / RUN_MS;
    const q = Math.min(1, Math.max(0, (p - 0.08) / 0.84));
    const x = Math.round(right ? 24 + q * 256 : 280 - q * 256);
    const anim = animFrame(this.ms);
    const edge = p < 0.08 || p > 0.92;
    const sprite = edge ? 'digbyTap' : right ? 'digbyRight' : 'digbyLeft';
    atlasFor(CAVE_A).draw(screen.ctx, sprite, anim >> 1, x, 70);
  }

  protected onAction(a: MenuAction): void {
    if (a === 'confirm') this.back();
  }
}
